// Story 10.4 — Empty state para analytics: el bot todavía no tiene datos.
// Story 13.x cleanup — adaptado para Evolution Go (mensajes distintos,
// quitado el botón "Copiar snippet" que era confuso porque solo copiaba
// el texto informativo del card, no un comando útil).

import { Link } from '@tanstack/react-router';
import { BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface Props {
  botId: number;
  phoneNumberId: string | null;
  /** Story 13.x — provider del bot ('cloud-api' | 'evolution-go') para
   *  mostrar el copy correcto. Default cloud-api por compat con callers
   *  que aún no lo pasan. */
  provider?: 'cloud-api' | 'evolution-go';
}

export function EmptyAnalytics({
  botId,
  phoneNumberId,
  provider = 'cloud-api',
}: Props): React.ReactElement {
  // Mensaje según el provider — Cloud API necesita phoneNumberId; Evolution
  // Go necesita instancia conectada (no phoneNumberId).
  let message: React.ReactNode;
  if (provider === 'evolution-go') {
    message = (
      <>
        Aún no hay conversaciones registradas. Verifica que la instancia esté{' '}
        <strong>conectada</strong> y envía un mensaje al número vinculado en tu Evolution Go para
        empezar.
      </>
    );
  } else if (phoneNumberId) {
    message = (
      <>
        El bot está configurado (
        <code className="rounded bg-muted px-1">phoneNumberId={phoneNumberId}</code>
        ). Envía un mensaje al número de WhatsApp asociado en tu Meta Business Manager para empezar.
      </>
    );
  } else {
    message = (
      <>
        Configura el número de WhatsApp del bot (Phone Number ID de Meta) para empezar a recibir
        mensajes.
      </>
    );
  }

  return (
    <Card className="mx-auto max-w-2xl">
      <CardHeader className="items-center text-center">
        <div className="rounded-full bg-primary/10 p-4">
          <BarChart3 className="h-8 w-8 text-primary" aria-hidden />
        </div>
        <CardTitle>Métricas en construcción</CardTitle>
        <CardDescription>
          Las métricas aparecerán cuando tu bot tenga conversaciones.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="rounded-md border border-border bg-muted/50 p-3 text-sm">{message}</p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button asChild variant="default">
            <Link to="/bots/$botId" params={{ botId: String(botId) }}>
              Volver a la página del bot
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/bots/$botId/conversations" params={{ botId: String(botId) }}>
              Ver conversaciones
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
